import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, LogOut, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Eyebrow, Loader, Logo, StatusBadge, ThemeToggle } from "@/components/common";
import { Contribution } from "@/components/member/Contribution";

const STEPS = ["Identité", "Entreprise", "Activité", "Apport et besoins", "Pièce d'identité"];
const MESSAGES = {
  avis_parrain: "Votre dossier est transmis à votre parrain, qui doit confirmer qu'il vous connaît.",
  soumis: "Votre dossier est transmis au bureau, qui va l'étudier.",
  en_etude: "Le bureau étudie votre dossier.",
  en_attente: "Votre dossier est mis en attente par le bureau. Nous revenons vers vous rapidement.",
  refuse: "Le bureau ne peut pas donner suite à votre candidature pour le moment.",
  expire: "Votre candidature a expiré faute d'avis du parrain. Contactez le bureau.",
  approuve: "Le bureau a approuvé votre dossier. Réglez votre première cotisation pour activer votre compte membre.",
};

const Chips = ({ options, value = [], onChange, max, testId }) => (
  <div className="flex flex-wrap gap-2">{options.map((o) => (
    <button type="button" key={o} data-testid={`${testId}-${o}`} onClick={() => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o].slice(0, max))}
      className={`px-3 h-9 rounded-full border text-xs ${value.includes(o) ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2"}`}>{o}</button>
  ))}</div>
);

function FileField({ kind, label, done, onUploaded, testId }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      onUploaded(await uploadFile(kind, file));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="mr-btn-secondary w-full cursor-pointer" data-testid={testId}>
      {done ? <Check size={16} className="text-mr-accent" /> : <Upload size={16} />} {busy ? "Envoi…" : done ? `${label} enregistrée` : label}
      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid={`${testId}-input`} />
    </label>
  );
}

function Wizard({ app, reload }) {
  const { settings } = useConfig();
  const [step, setStep] = useState(0);
  const [d, setD] = useState(app.dossier || {});
  const [files, setFiles] = useState({ photo_file_id: app.photo_file_id, id_document_file_id: app.id_document_file_id });
  const [saved, setSaved] = useState("");
  const timer = useRef(null);
  const pending = useRef({});

  const save = useCallback(async (patch) => {
    try {
      await api.patch("/me/application", patch);
      setSaved("Enregistré automatiquement");
    } catch (e) {
      setSaved(errMsg(e));
    }
  }, []);
  const set = (k, v) => {
    const next = { ...d, [k]: v };
    setD(next);
    setSaved("Enregistrement…");
    clearTimeout(timer.current);
    pending.current = { ...pending.current, [k]: v };
    timer.current = setTimeout(() => {
      const batch = pending.current;
      pending.current = {};
      save({ dossier: batch });
    }, 700);
  };
  const setFile = (k, id) => {
    setFiles({ ...files, [k]: id });
    save({ [k]: id });
  };
  const submit = async () => {
    try {
      clearTimeout(timer.current);
      pending.current = {};
      await api.patch("/me/application", { dossier: d });
      await api.post("/me/application/submit");
      toast.success("Dossier transmis");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const txt = (k, label, props = {}) => (
    <label className="block"><span className="mr-label">{label}</span><input data-testid={`dossier-${k}-input`} className="mr-input" value={d[k] ?? ""} onChange={(e) => set(k, e.target.value)} {...props} /></label>
  );
  const area = (k, label) => (
    <label className="block"><span className="mr-label">{label}</span><textarea data-testid={`dossier-${k}-input`} className="mr-input min-h-[100px]" value={d[k] ?? ""} onChange={(e) => set(k, e.target.value)} /></label>
  );
  const select = (k, label, options) => (
    <label className="block"><span className="mr-label">{label}</span><select data-testid={`dossier-${k}-select`} className="mr-input" value={d[k] ?? ""} onChange={(e) => set(k, e.target.value)}>
      <option value="">Choisir…</option>{options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</select></label>
  );
  const panels = [
    <div className="space-y-4" key="0">
      <p className="text-sm text-mr-text2">Votre photo sera visible des membres une fois votre adhésion activée.</p>
      <FileField kind="photo" label="Photo de profil" done={!!files.photo_file_id} onUploaded={(id) => setFile("photo_file_id", id)} testId="dossier-photo" />
      {area("bio", "Présentez-vous en quelques lignes")}
      <div><span className="mr-label">Langues parlées</span><Chips testId="dossier-lang" options={settings["catalog.languages"] || []} value={d.languages || []} onChange={(v) => set("languages", v)} max={6} /></div>
    </div>,
    <div className="space-y-4" key="1">
      {txt("company", "Nom de l'entreprise")}
      {select("city", "Ville / commune", settings["catalog.cities"] || [])}
      {txt("country", "Pays", { placeholder: "Côte d'Ivoire" })}
      {select("legal_status", "Statut juridique", settings["catalog.legal_statuses"] || [])}
      {txt("years_in_business", "Années d'activité", { type: "number", min: 0, inputMode: "numeric" })}
    </div>,
    <div className="space-y-4" key="2">
      <div><span className="mr-label">Secteurs (5 maximum)</span><Chips testId="dossier-sector" options={settings["catalog.sectors"] || []} value={d.sectors || []} onChange={(v) => set("sectors", v)} max={5} /></div>
      <div><span className="mr-label">Catégories de produits</span><Chips testId="dossier-category" options={settings["catalog.product_categories"] || []} value={d.product_categories || []} onChange={(v) => set("product_categories", v)} max={8} /></div>
      {select("trade_mode", "Vous vendez", [{ value: "gros", label: "En gros" }, { value: "detail", label: "Au détail" }, { value: "gros_detail", label: "En gros et au détail" }])}
      {select("importing", "Importation", [{ value: "actuelle", label: "J'importe déjà" }, { value: "envisagee", label: "J'envisage d'importer" }, { value: "non", label: "Non" }])}
      <div><span className="mr-label">Pays d'approvisionnement</span><Chips testId="dossier-country" options={settings["catalog.countries"] || []} value={d.sourcing_countries || []} onChange={(v) => set("sourcing_countries", v)} max={8} /></div>
    </div>,
    <div className="space-y-4" key="3">{area("brings", "Ce que j'apporte au réseau")}{area("seeks", "Ce que je cherche")}</div>,
    <div className="space-y-4" key="4">
      <p className="text-sm text-mr-text2">Votre pièce d'identité est chiffrée et visible uniquement par les administrateurs du club.</p>
      <FileField kind="id_document" label="Pièce d'identité" done={!!files.id_document_file_id} onUploaded={(id) => setFile("id_document_file_id", id)} testId="dossier-id" />
      <button data-testid="dossier-submit-button" className="mr-btn-primary w-full" onClick={submit}>Transmettre mon dossier</button>
    </div>,
  ];
  return (
    <div className="mr-card mt-6 space-y-5" data-testid="dossier-wizard">
      {app.status === "info_demandee" && <p className="text-sm border border-mr-warning text-mr-warning p-3 rounded-[3px]" data-testid="dossier-info-request">Le bureau demande : {app.info_request}</p>}
      <div className="flex gap-1">{STEPS.map((s, k) => <button key={s} onClick={() => setStep(k)} data-testid={`dossier-step-${k}`} aria-label={s} className={`h-1.5 flex-1 rounded-full ${k <= step ? "bg-mr-accent" : "bg-mr-border"}`} />)}</div>
      <div className="flex items-center justify-between"><p className="font-medium">{step + 1}. {STEPS[step]}</p><span className="text-xs text-mr-muted" data-testid="dossier-autosave">{saved}</span></div>
      {panels[step]}
      <div className="flex justify-between gap-3">
        <button className="mr-btn-ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="dossier-prev-button">Précédent</button>
        {step < STEPS.length - 1 && <button className="mr-btn-secondary" onClick={() => setStep(step + 1)} data-testid="dossier-next-button">Suivant</button>}
      </div>
    </div>
  );
}

export default function Candidate() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const load = () => api.get("/me/application").then(({ data }) => setApp(data)).catch(() => setApp(false));
  useEffect(() => {
    load();
  }, []);
  const editable = app && ["brouillon", "info_demandee"].includes(app.status);
  return (
    <div className="min-h-screen mr-grid-bg">
      <header className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
        <Logo />
        <div className="flex gap-2"><ThemeToggle />
          <button data-testid="candidate-logout-button" className="mr-btn-ghost" onClick={async () => { await logout(); nav("/"); }}><LogOut size={16} /> Quitter</button></div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12 mr-fade-up">
        <Eyebrow className="mb-4">Votre candidature</Eyebrow>
        <h1 className="mr-h2 sm:text-4xl">Bonjour {me.user.name.split(" ")[0]}</h1>
        {app === null ? <Loader /> : !app ? <p className="mt-6 text-mr-text2">Aucun dossier trouvé.</p> : (
          <>
            <div className="mr-card mt-8 flex items-center justify-between gap-3" data-testid="candidate-status-card">
              <div><p className="font-medium">Statut du dossier</p>{app.sponsor_name && <p className="text-sm text-mr-muted">Parrain : {app.sponsor_name}</p>}</div>
              <StatusBadge status={app.status} testId="candidate-application-status" />
            </div>
            {editable ? <Wizard app={app} reload={load} /> : (
              <div className="mr-card mt-4 space-y-4">
                <p className="text-sm text-mr-text2" data-testid="candidate-status-message">{MESSAGES[app.status]}{app.status === "refuse" && app.reason ? ` Motif : ${app.reason}` : ""}</p>
                {app.status === "approuve" && <Contribution compact={false} />}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
